import {
    type MouseEvent,
    type MutableRefObject,
    type TouchEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { type AxiosError } from 'axios'
import dayjs from 'dayjs'
import { isEmpty, isUndefined, omitBy } from 'lodash'
import { useTranslation } from 'react-i18next'
import {
    type QueryKey,
    type UseMutationOptions,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient
} from 'react-query'
import { useLocation } from 'react-router-dom'

import axiosInstance from 'api/axiosInstance'
import { addToast } from 'components/Toast/Toast'
import { useTrackingContext } from 'context/trackingContext'
import { rollbackToggleLikeInCaches, toggleLikeInCaches } from 'helpers/notificationscache/toggleLikeInCaches'
import { type UserProfile } from 'models'
import { getDiscoveryFeed, getHomeFeed, getUsersFeedInfinite} from 'services/endpoints/feed'
import {
    getNotifications,
    getUserActivity
} from 'services/endpoints/notification'
import {
    answerPoll,
    deletePost,
    getPostCommentsInfinite,
    getSinglePost,
    pinPost,
    postStorySeen,
    sendTipToPost
} from 'services/endpoints/post'
import { getProfile } from 'services/endpoints/profile'
import { reportPost } from 'services/endpoints/report'
import { changeLinkedAccount } from 'services/endpoints/settings/account/profileSettings'
import { getAllStories, getStoryFeed, likeStory, reportStory, unlikeStory } from 'services/endpoints/story'
import {
    apiFanProfileSession,
} from 'services/endpoints/tracking'
import { useAccessToken, useIsLoggingOut, useUserProfile } from 'store'
import { type ReportPost } from 'types/api/report'
import { type Story, type StoryFeedGroup } from 'types/api/story'
import { type IFile } from 'types/interfaces/IFile'
import { type IPreviewFile } from 'types/interfaces/IPreviewFile'
import {
    type IComment,
    type IHomeFeedCursorPage,
    type IInfiniteCommentsPage,
    type IInfiniteNotifications,
    type IInfinitePage,
    type IPost,
    type IPostMediaCounter,
    type IPostsFeedCursorPage,
    type ITipPayload
} from 'types/interfaces/ITypes'
import { type LikeableType, type StoryActionType } from 'types/types'
import { useLocalStorageSetAccessToken } from 'utils'

import { useRefetchUserProfile } from './hooks/useRefetchUserProfile/useRefetchUserProfile'
import { orderMediaFiles } from './media'
import { updatePostPollAnswer } from './post'

export const useMediaCounters = (postData: IPost) => {
  const { media, previews } = useMemo(() => {
    return orderMediaFiles(postData)
  }, [
    // best: use post id + lengths to avoid ref churn
    postData?.id,
    postData?.photos?.length,
    postData?.videos?.length,
    postData?.sounds?.length,
    postData?.photos_preview?.length,
    postData?.videos_preview?.length,
  ])

  // totals derived from postData, always stable
  const imgsTotal = postData?.photos?.length ? postData.photos.length : null
  const vidsTotal = postData?.videos?.length ? postData.videos.length : null

  // track "current index" only
  const [img, setImg] = useState<number | null>(null)
  const [vid, setVid] = useState<number | null>(null)

  // main counter output
  const counter: IPostMediaCounter = useMemo(() => {
    return {
      imgs: imgsTotal,
      img,
      vids: vidsTotal,
      vid,
    }
  }, [imgsTotal, vidsTotal, img, vid])

  const handleUpdateCounters = useCallback(
    (key: number) => {
      const file: IFile | undefined = media?.[key]
      if (!file?.url) return

      const isImage = file.url.includes('/image')
      const isVideo = file.url.includes('/video')

      if (isImage) {
        const imageIndex =
          media.filter((item: any) => item?.url?.includes('/image')).indexOf(file) + 1

        setImg(imageIndex > 0 ? imageIndex : null)
        setVid(null)
      } else if (isVideo) {
        const videoIndex =
          media.filter((item: any) => item?.url?.includes('/video')).indexOf(file) + 1

        setVid(videoIndex > 0 ? videoIndex : null)
        setImg(null)
      }
    },
    [media]
  )

  const handleUpdatePreviewCounters = useCallback(
    (key: number) => {
      const pf: any | undefined = previews?.[key]
      const src: string | undefined = pf?.preview?.src
      if (!src) return

      const isImage = src.includes('/image')
      const isVideo = src.includes('/video')

      if (isImage) {
        const imageIndex =
          previews
            .filter((item: any) => item?.preview?.src?.includes('/image'))
            .indexOf(pf) + 1

        setImg(imageIndex > 0 ? imageIndex : null)
        setVid(null)
      } else if (isVideo) {
        const videoIndex =
          previews
            .filter((item: any) => item?.preview?.src?.includes('/video'))
            .indexOf(pf) + 1

        setVid(videoIndex > 0 ? videoIndex : null)
        setImg(null)
      }
    },
    [previews]
  )

  // If you need preview totals instead when not purchased:
  // you can decide which totals to show at the callsite based on postData.is_purchased

  return {
    counter,
    handleUpdateCounters,
    handleUpdatePreviewCounters,
  }
}

export const useFilterQuery = () => {
    const { search } = useLocation()

    return useMemo(() => new URLSearchParams(search), [search])
}

export const useQueryParams = <T>() => {
    const { search } = useLocation()

    return useMemo<T>(
        () => Object.fromEntries(new URLSearchParams(search).entries()) as T,
        [search]
    )
}

export const useDynamicHeight = (offset: number) => {
    const [wrapperStyle, setWrapperStyle] = useState<any>(null)
    useLayoutEffect(() => {
        const height = window.innerHeight - offset
        setWrapperStyle({ height: `${height / 10}rem` })
    }, [])

    return { wrapperStyle }
}

/**
 * Hook that alerts clicks outside of the passed ref
 */
function hasClass(elem: HTMLElement, cls: string) {
    const str = ' ' + elem.className + ' '
    const testCls = ' ' + cls + ' '
    return str.includes(testCls)
}

function closest(el: any, cls: string) {
    while (el && el !== document.body) {
        if (hasClass(el, cls)) return el
        el = el.parentNode
    }
    return null
}

export const useOutsideAlerter = (
    ref: MutableRefObject<HTMLElement | null>,
    cb: () => void,
    excludeClasses?: string[]
) => {
    useEffect(() => {
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event: Event) {
            const el = event.target as HTMLElement

            if (ref?.current && !ref.current.contains(event.target as Node)) {
                if (excludeClasses && !(excludeClasses.length === 0)) {
                    const isSendButton = excludeClasses.some(excClass =>
                        closest(el, excClass)
                    )

                    if (!isSendButton) {
                        cb()
                    }
                } else {
                    cb()
                }
            }
        }

        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [ref])
}

// Hook
// T is a generic type for value parameter, our case this will be string
export function useDebounce<T>(value: T, delay: number): T {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState<T>(value)
    useEffect(
        () => {
            // Update debounced value after delay
            const handler = setTimeout(() => {
                setDebouncedValue(value)
            }, delay)
            // Cancel the timeout if value changes (also on delay change or unmount)
            // This is how we prevent debounced value from updating if value is changed ...
            // .. within the delay period. Timeout gets cleared and restarted.
            return () => {
                clearTimeout(handler)
            }
        },
        [value, delay] // Only re-call effect if value or delay changes
    )
    return debouncedValue
}

export function debounce<Params extends any[]>(
    func: (...args: Params) => any,
    timeout: number
): (...args: Params) => void {
    let timer: NodeJS.Timeout
    return (...args: Params) => {
        clearTimeout(timer)
        timer = setTimeout(() => {
            func(...args)
        }, timeout)
    }
}


type FeedFilter = Record<string, any> | null | undefined

type ExtraOptions = {
  enabled?: boolean
  refetchOnMount?: boolean | 'always'
  refetchOnWindowFocus?: boolean
  staleTime?: number
}

export const usePostsFeed = (
  userId: string | undefined,
  filter: FeedFilter,
  initialCachedPosts?: IPost[],
  isSubscribed?: boolean,
  cachedUpdatedAt?: number,
  opts?: ExtraOptions
) => {
  const isLoggingOut = useIsLoggingOut()

  const hasFilter = filter != null
  const filteredFilter = hasFilter
    ? omitBy(filter as Record<string, unknown>, (v) => {
        if (isUndefined(v) || v === '') return true
        if (typeof v === 'object' && isEmpty(v)) return true
        return false
      })
    : null

  const queryKey = hasFilter
    ? (['getFeed', userId, filteredFilter] as const)
    : (['getFeed', userId] as const)

  const hasCache = !!initialCachedPosts?.length

  const baseEnabled =
    !!userId &&
    isSubscribed === true &&
    !isLoggingOut

  const enabled = baseEnabled && (opts?.enabled ?? true)

    const staleTime = opts?.staleTime ?? 5 * 60 * 1000

    const refetchOnMount = enabled
    ? opts?.refetchOnMount ?? true
    : false

    const refetchOnWindowFocus =
    opts?.refetchOnWindowFocus ?? true

  return useInfiniteQuery<IPostsFeedCursorPage, Error>(
    queryKey,
    async ({ pageParam, signal }) => {
      const cursor = typeof pageParam === 'string' ? pageParam : undefined

      return await getUsersFeedInfinite(
        userId as string,
        {
          ...(filteredFilter ?? {}),
          ...(cursor ? { cursor } : {}),
        },
        signal
      )
    },
    {
      getNextPageParam: (lastPage) => {
        const nextCursor = (lastPage as any)?.nextCursor
        return nextCursor ?? undefined
      },

    enabled,
    keepPreviousData: false,
    placeholderData: undefined,
    staleTime,
    refetchOnMount,
    refetchOnWindowFocus,
    refetchOnReconnect: true,
    refetchInterval: enabled
    ? 30 * 60 * 1000
    : false,
    refetchIntervalInBackground: false,

      initialData: hasCache
        ? ({
            pages: [
              {
                page: {
                  data: {
                    status: 'OK',
                    code: 200,
                    data: initialCachedPosts,
                    meta: {
                      next_cursor: null,
                      prev_cursor: null,
                      per_page: initialCachedPosts.length,
                      path: '',
                    },
                    links: {
                      next: null,
                      prev: null,
                      first: null,
                      last: null,
                    },
                  },
                },
                nextCursor: undefined,
              },
            ],
            pageParams: [undefined],
          } as any)
        : undefined,

        initialDataUpdatedAt: hasCache
        ? cachedUpdatedAt ?? 0
        : undefined,
    }
  )
}
type UseHomeFeedParams = {
  list: string
  enabled?: boolean
  type?: string
  per_page?: number
  refetchOnMount?: boolean
}

export const useHomePostsFeed = (params: UseHomeFeedParams) => {
  const enabled = params.enabled ?? true

  const query = useInfiniteQuery<IHomeFeedCursorPage, Error>(
    ['getHomeFeed', params.list, params.type ?? 'all'],
    async ({ pageParam }) => {
      const cursor = (pageParam ?? null) as string | null

      return await getHomeFeed(params, cursor)
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.page.data.meta?.next_cursor ?? undefined,

      enabled,

        refetchOnMount: params.refetchOnMount ?? true,

        refetchOnWindowFocus: true,
        refetchOnReconnect: true,

        staleTime: 5 * 60 * 1000,

        refetchInterval: enabled
        ? 30 * 60 * 1000
        : false,

        refetchIntervalInBackground: false,
    }
  )

  const posts: IPost[] = useMemo(() => {
    return (
      query.data?.pages?.flatMap(
        page => page.page.data.data ?? []
      ) ?? []
    )
  }, [query.data])

  return {
    ...query,
    posts
  }
}
type UseDiscoveryFeedParams = {
  list: string
  enabled?: boolean
  type?: string
  per_page?: number
}

export const useDiscoveryPostsFeed = (params: UseDiscoveryFeedParams) => {
  const enabled = params.enabled ?? true

  const query = useInfiniteQuery<IHomeFeedCursorPage, Error>(
    ['getDiscoveryFeed', params.list, params.type ?? 'all'],
    async ({ pageParam }) => {
      const cursor = (pageParam ?? null) as string | null
      return await getDiscoveryFeed(params, cursor)
    },
    {
      getNextPageParam: (lastPage) => lastPage.page.data.meta?.next_cursor ?? undefined,
      enabled,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 20_000,
      cacheTime: 20_000,
    }
  )

  const posts: IPost[] = useMemo(() => {
    return query.data?.pages?.flatMap((p) => p.page.data.data ?? []) ?? []
  }, [query.data])

  return { ...query, posts }
}

/* export const useHomePostsFeedLite = (params: UseHomeFeedParams) => {
  const enabled = params.enabled ?? true

  const queryKey = useMemo(
    () => ['getHomeFeedLite', params.list, params.type ?? 'all', params.per_page ?? 15] as const,
    [params.list, params.type, params.per_page]
  )
  const query = useInfiniteQuery<IHomeFeedLitePage, Error, IHomeFeedLitePage, typeof queryKey>(
    queryKey,
    async ({ pageParam }) => {
      const cursor = (pageParam ?? null) as string | null
      return await getHomeFeedLite(params, cursor) //  must return Promise<IHomeFeedLitePage>
    },
    {
      getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
      enabled,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      keepPreviousData: true,
    }
  )

  const posts = useMemo(() => {
    return query.data?.pages?.flatMap((p) => p.posts ?? []) ?? []
  }, [query.data])

  return { ...query, posts }
}
 */
export const useDeletePost = (options = {}) => {
    const deletePostMutation = useMutation(
        async (postId: string) => await deletePost(postId),
        options
    )

    return { deletePostMutation }
}

export const useLikePost = (
  postId: string,
  isLiked: boolean,
  onMutateCb?: () => any,
  onErrorCb?: (_err?: any, _context?: any) => void,
  onSettledCb?: () => void,
  onSuccessCb?: () => void
) => {
  const likePost = useMutation(
    async () => {
      await axiosInstance({
        method: isLiked ? 'delete' : 'post',
        url: `/api/like/post/${postId}`
      })
    },
    {
      onMutate: () => {
        return onMutateCb?.()
      },
      onError: (err, _vars, context) => {
        onErrorCb?.(err, context)
      },
      onSettled: () => {
        onSettledCb?.()
      },
      onSuccess: () => {
        onSuccessCb?.()
      }
    }
  )

  return { likePost }
}
export const useLiking = (
  likeableId: number,
  likeableType: LikeableType,
  notifFilterKey?: string,
  notificationType?: string,
  onErrorCb?: (error: any) => void,
  onSuccessCb?: (data: any) => void,
  onSettledCb?: () => void
) => {
  const queryClient = useQueryClient()

  const like = useMutation(
    async () => {
        await axiosInstance({
        method: 'post',
        url: `/api/like/${likeableType}/${likeableId}`,
        data: {
            notification_type: notificationType,
        },
        })
    },
    {
      onMutate: async () => {
        // cancel only notifications to avoid stomping other optimistic updates
        if (likeableType === 'notification') {
          await queryClient.cancelQueries(['notifications'])
        }

        // figure out current liked from any notifications cache
        let currentLiked = false

        if (likeableType === 'notification') {
          const notifQueries = queryClient.getQueryCache().findAll(['notifications'])
          let found = false

          for (const q of notifQueries) {
            const data: any = queryClient.getQueryData(q.queryKey)
            const pages = data?.pages
            if (!Array.isArray(pages)) continue

            for (const p of pages) {
              const list = p?.page?.data?.data
              if (!Array.isArray(list)) continue

              const hit = list.find((n: any) => n?.id === likeableId)
              if (hit) {
                currentLiked = !!hit.is_liked
                found = true
                break
              }
            }

            if (found) break
          }
        }

        const nextLiked = !currentLiked

        const { snapshot } = toggleLikeInCaches(queryClient, {
          likeableType,
          likeableId,
          nextLiked,
          notifFilterKey
        })

        return { snapshot }
      },

      onError: (error, _vars, ctx: any) => {
        if (ctx?.snapshot) rollbackToggleLikeInCaches(queryClient, ctx.snapshot)
        onErrorCb?.(error)
      },

      onSuccess: data => {
        onSuccessCb?.(data)
      },

      onSettled: () => {
        // optional: refresh first page later if you want server-truth
        // if (likeableType === 'notification') queryClient.invalidateQueries(['notifications'])
        onSettledCb?.()
      }
    }
  )

  return { like }
}


export const useTipPost = (options: {
    onMutate: (val: number) => void
    onError?: () => void
}) => {
    const { refetchUserProfile } = useRefetchUserProfile()

    const setPostTip = useMutation(
        async (variables: ITipPayload) => await sendTipToPost(variables),
        {
            onMutate: variables => {
                options.onMutate(parseInt(variables.amount))
            },
            onSuccess: () => {
                refetchUserProfile()
            }
        }
    )

    return { setPostTip }
}
export const usePinPost = (options: any) => {
    const pin = useMutation(
        async (postId: string) => await pinPost(postId),
        options
    )

    return { pin }
}
export const useAnswerPoll = (
  dataQuery: any,
  postId: string
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const setPollAnswer = useMutation(
    async (variables: {
      pollId: string
      answer?: string | number | null
      answerId?: string
    }) => {
      await answerPoll(variables)
    },
    {
      onMutate: async variables => {
        await queryClient.cancelQueries(dataQuery)

        const previousData =
          queryClient.getQueryData(dataQuery)

        updatePostPollAnswer(
          postId,
          dataQuery,
          queryClient,
          variables.answerId ??
            String(variables.answer)
        )

        return {
          previousData
        }
      },

      onError: (
        error: AxiosError,
        _variables,
        context
      ) => {
        if (context?.previousData !== undefined) {
          queryClient.setQueryData(
            dataQuery,
            context.previousData
          )
        }

        if (error?.response?.status === 422) {
          addToast(
            'error',
            t('error:restrictedAnswerPoll')
          )
        }
      }
    }
  )

  return {
    setPollAnswer
  }
}

export const useCommentsInfinite = (
    postId: string,
    initialComments: IComment[],
    fetchCommentsEnabled: boolean = false
) => {
    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        status
    } = useInfiniteQuery<IInfiniteCommentsPage, Error>(
        ['commentsForPost', postId],
        async ({ pageParam }) =>
            await getPostCommentsInfinite(postId, pageParam),
        {
            getNextPageParam: lastPage => {
                return lastPage.nextCursor
            },
            refetchOnWindowFocus: false,
            enabled: fetchCommentsEnabled,
            initialData: {
                pageParams: [],
                pages: [
                    {
                        nextCursor: 1,
                        page: {
                            data: {
                                data: initialComments
                            }
                        }
                    }
                ]
            }
        }
    )

    return {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        status
    }
}

export const useDeleteComment = (options = {}) => {
    const deleteComment = useMutation(
        async (data: {
            commentId: string
            parentId: string
            isReply: boolean
        }) => {
            await axiosInstance({
                method: 'delete',
                url: `/api/comment/${data.commentId}`
            })
        },
        options
    )

    return { deleteComment }
}

export const useReportComment = (options = {}) => {
    const reportComment = useMutation(
        async (data: {
            commentId: string
            message: string
            reportType: number
        }) => {
            await axiosInstance({
                method: 'post',
                url: `/api/report/comment/${data.commentId}`,
                data: {
                    message: data.message,
                    report_type_id: data.reportType
                }
            })
        },
        options
    )

    return { reportComment }
}
export const useReportPost = (options = {}) => {
    const reportPostMutation = useMutation(
        async (variables: ReportPost) => await reportPost(variables),
        options
    )

    return { reportPostMutation }
}


export const useReportStory = (options = {}) => {
  const reportStoryMutation = useMutation(reportStory, options)
  return { reportStoryMutation }
}

export const useStorySeen = (options = {}) => {
    const storySeenMutation = useMutation(
        async (variables: { storyId: string }) =>
            await postStorySeen(variables.storyId),
        options
    )

    return { storySeenMutation }
}
export const useStoryLike = (options = {}) => {
  const storyLikeMutation = useMutation(
    async (variables: { storyId: string }) =>
      await likeStory(variables.storyId),
    options
  )

  const storyUnlikeMutation = useMutation(
    async (variables: { storyId: string }) =>
      await unlikeStory(variables.storyId),
    options
  )

  return {
    storyLikeMutation,
    storyUnlikeMutation
  }
}
export const useChangeAccount = () => {
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    const changeAccount = useMutation(
        async (linkedId: any) => await changeLinkedAccount(linkedId),
        {
            onSuccess: function (data) {
                useLocalStorageSetAccessToken(data.data.message.token)
                queryClient.invalidateQueries()

                addToast('success', t('accountSuccessfullyChanged'))
            },
            onError: function (data) {
                console.log(data)
                addToast('error', t('error:errorSomethingWentWrong'))
            }
        }
    )
    return { changeAccount }
}
export const useAutoplay = (options: any) => {
    const autoplay = useMutation(async () => {
        await axiosInstance({
            method: 'post',
            url: '/api/user/autoplay'
        })
    }, options)

    return { autoplay }
}

export const usePreviousState = (value: any) => {
    const ref = useRef()
    useEffect(() => {
        ref.current = value
    }, [value])

    return ref.current
}

export const useNotificationsFeedInfinite = (filter: string) => {
  const query = useInfiniteQuery<IInfiniteNotifications, Error>(
    ['notifications', filter],
    //                          ↓↓↓ include signal from context
    async ({ pageParam = 1, signal }) => await getNotifications(pageParam, filter, signal),
    {
      getNextPageParam: lastPage => lastPage?.nextCursor ?? undefined,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      /* staleTime: 5 * 60 * 1000, */
      // cacheTime: 30 * 60 * 1000
    }
  )

  const refetchFirstPage = async () =>
    await query.refetch({
      refetchPage: (_page: IInfiniteNotifications, index: number) => index === 0,
      throwOnError: false
    })

  return {
    data: query.data,
    error: query.error ?? null,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    status: query.status,
    refetch: query.refetch,
    refetchFirstPage,
    dataUpdatedAt: query.dataUpdatedAt
  }
}

export const useEventDefiner = ({
    onClick,
    onSwipeLeft,
    onSwiperight,
    onLongPressStart,
    onLongPressEnd
}: {
    onClick?: () => any
    onSwipeLeft?: () => any
    onSwiperight?: () => any
    onLongPressStart?: () => any
    onLongPressEnd?: () => any
}) => {
    const [action, setAction] = useState<StoryActionType>('click')

    const actionOriginRef = useRef<'mouse' | 'touch' | ''>('')

    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const isAboveClickThreshold = useRef<boolean>(false)
    const mouseInitialPositionRef = useRef<{ x: number; y: number }>({
        x: 0,
        y: 0
    })

    function startPressTimer() {
        timerRef.current = setTimeout(() => {
            isAboveClickThreshold.current = true
        }, 700)
    }

    function initializeMouseLocation(event: MouseEvent | TouchEvent): {
        x: number
        y: number
    } {
        let tempMousePosition = { x: 0, y: 0 }

        if (event.type === 'mousedown' || event.type === 'mouseup') {
            event = event as MouseEvent
            tempMousePosition = { x: event.clientX, y: event.clientY }
        } else if (event.type === 'touchstart') {
            event = event as TouchEvent
            tempMousePosition = {
                x: event.touches[0].clientX,
                y: event.touches[0].clientY
            }
        } else if (event.type === 'touchend') {
            event = event as TouchEvent
            tempMousePosition = {
                x: event.changedTouches[0].clientX,
                y: event.changedTouches[0].clientY
            }
        }
        return tempMousePosition
    }

    function handleOnMouseDown(event: MouseEvent | TouchEvent) {
        if (timerRef.current !== null) return
        mouseInitialPositionRef.current = initializeMouseLocation(event)
        onLongPressStart?.()
        startPressTimer()
    }

    function handleOnMouseUp(event: MouseEvent | TouchEvent) {
    const endPosition = initializeMouseLocation(event)
    const startPosition = mouseInitialPositionRef.current

    const deltaX = startPosition.x - endPosition.x
    const deltaY = startPosition.y - endPosition.y

    const absX = Math.abs(deltaX)
    const absY = Math.abs(deltaY)

    if (!isAboveClickThreshold.current) {
        // A horizontal swipe must be clearly more horizontal
        // than vertical. Otherwise, leave navigation to the
        // parent vertical Swiper.
        if (absX > 40 && absX > absY) {
        if (deltaX > 0) {
            setAction('swipeRight')
            onSwiperight?.()
        } else {
            setAction('swipeLeft')
            onSwipeLeft?.()
        }
        } else if (absX <= 40 && absY <= 40) {
        // Only a genuine tap/click triggers the click action.
        setAction('click')
        onClick?.()
        }
        // Vertical swipes do not trigger story navigation or clicks.
    } else {
        setAction('longPress')
        onLongPressEnd?.()
    }

    isAboveClickThreshold.current = false

    if (timerRef.current === null) return

    clearTimeout(timerRef.current)
    timerRef.current = null
    }

    return {
        action,
        handlers: {
            // onClick: handleOnClick,
            onMouseDown: (event: MouseEvent) => {
                actionOriginRef.current =
                    actionOriginRef.current === ''
                        ? 'mouse'
                        : actionOriginRef.current
                actionOriginRef.current === 'mouse' && handleOnMouseDown(event)
            },
            onMouseUp: (event: MouseEvent) => {
                actionOriginRef.current === 'mouse' && handleOnMouseUp(event)
            },
            onTouchStart: (event: TouchEvent) => {
                actionOriginRef.current = 'touch'
                actionOriginRef.current === 'touch' && handleOnMouseDown(event)
            },
            onTouchEnd: (event: TouchEvent) => {
                actionOriginRef.current === 'touch' && handleOnMouseUp(event)
            },
            onContextMenu: (event: MouseEvent) => {
                event.preventDefault()
            }
        }
    }
}

export const useInfiniteFeed = (
  queryParams: [string, any],
  fetchFn: (...args: any[]) => Promise<IInfinitePage>,
  p0?: { enabled: boolean } // <-- make optional
) => {
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  } = useInfiniteQuery<IInfinitePage, Error>(
    queryParams,
    async ({ pageParam = 1 }) => {
      return await fetchFn({ ...queryParams[1], page: pageParam })
    },
    {
      getNextPageParam: lastPage => {
        return lastPage.nextCursor
      },
      refetchOnWindowFocus: false,
      // keep your original condition AND allow caller override
      enabled: (!!queryParams && !!queryParams.length) && (p0?.enabled ?? true)
    }
  )

  return {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  }
}
export const useGetMonthName = () => {
    const { t } = useTranslation()

    const getMonthName = (
        dateIndex: number,
        type: 'full' | 'shorthand' = 'full'
    ) => {
        switch (dateIndex) {
            case 0:
                if (type === 'full') {
                    return t('january')
                } else {
                    return t('jan')
                }
            case 1:
                if (type === 'full') {
                    return t('february')
                } else {
                    return t('feb')
                }
            case 2:
                if (type === 'full') {
                    return t('march')
                } else {
                    return t('mar')
                }
            case 3:
                if (type === 'full') {
                    return t('april')
                } else {
                    return t('apr')
                }
            case 4:
                if (type === 'full') {
                    return t('mayFull')
                } else {
                    return t('may')
                }
            case 5:
                if (type === 'full') {
                    return t('june')
                } else {
                    return t('jun')
                }
            case 6:
                if (type === 'full') {
                    return t('july')
                } else {
                    return t('Jul')
                }
            case 7:
                if (type === 'full') {
                    return t('august')
                } else {
                    return t('aug')
                }
            case 8:
                if (type === 'full') {
                    return t('september')
                } else {
                    return t('sept')
                }
            case 9:
                if (type === 'full') {
                    return t('october')
                } else {
                    return t('oct')
                }
            case 10:
                if (type === 'full') {
                    return t('november')
                } else {
                    return t('nov')
                }
            case 11:
                if (type === 'full') {
                    return t('december')
                } else {
                    return t('dec')
                }

            default:
                if (type === 'full') {
                    return t('january')
                } else {
                    return t('januaryShorthand')
                }
        }
    }

    return {
        getMonthName: (dateIndex: number, type: 'full' | 'shorthand') =>
            getMonthName(dateIndex, type)
    }
}

export const useWeekday = () => {
    const { t } = useTranslation()

    const getWeekday = (
        weekdayIndex: number,
        type: 'full' | 'shorthand' = 'full'
    ) => {
        switch (weekdayIndex) {
            case 0:
                if (type === 'full') {
                    return t('monday')
                } else {
                    return t('mondayShorthand')
                }
            case 1:
                if (type === 'full') {
                    return t('tuesday')
                } else {
                    return t('tuesdayShorthand')
                }
            case 2:
                if (type === 'full') {
                    return t('wednesday')
                } else {
                    return t('wednesdayShorthand')
                }
            case 3:
                if (type === 'full') {
                    return t('thursday')
                } else {
                    return t('thursdayShorthand')
                }
            case 4:
                if (type === 'full') {
                    return t('friday')
                } else {
                    return t('fridayShorthand')
                }
            case 5:
                if (type === 'full') {
                    return t('saturday')
                } else {
                    return t('saturdayShorthand')
                }
            case 6:
                if (type === 'full') {
                    return t('sunday')
                } else {
                    return t('sundayShorthand')
                }
            default:
                if (type === 'full') {
                    return t('monday')
                } else {
                    return t('mondayShorthand')
                }
        }
    }

    return {
        getWeekday: (weekdayIndex: number, type: 'full' | 'shorthand') =>
            getWeekday(weekdayIndex, type)
    }
}

// Convert state (or any other reactive...) to ref and update on state change
// similar purpose to upcoming react useEvent when used in useEffect
// (will behave like it isn't in dependencies cause it won't change troughout the lifecycle of component)
export const useSelfUpdatingRef = <T>(reactive: T) => {
    const ref = useRef<T>(reactive)

    useLayoutEffect(() => {
        ref.current = reactive
    }, [reactive])

    return ref
}



// VIDEO PLAYS TRACKING

export type VideoPlayedPayload = {
    video_id: string
    total_watch_seconds: number
    unique_watch_seconds: number
    completion_rate: number
    video_duration_seconds: number
    is_preview: boolean
}

type WatchRange = {
    start: number
    end: number
}

const mergeRanges = (ranges: WatchRange[]): WatchRange[] => {
    if (!ranges.length) return []

    const sorted = [...ranges]
        .filter(r => Number.isFinite(r.start) && Number.isFinite(r.end) && r.end > r.start)
        .sort((a, b) => a.start - b.start)

    const merged: WatchRange[] = []

    for (const range of sorted) {
        const last = merged[merged.length - 1]

        if (!last || range.start > last.end) {
            merged.push({ ...range })
        } else if (range.end > last.end) {
            last.end = range.end
        }
    }

    return merged
}

const sumRanges = (ranges: WatchRange[]): number => {
    return ranges.reduce((sum, r) => sum + Math.max(0, r.end - r.start), 0)
}

export const useVideoPlayedTracking = (
    trackPlayed: boolean = false,
    url: string,
    videoId: string | undefined,
    videoUserId: string | undefined,
    videoDurationSeconds?: number,
    isPreview: boolean = false
) => {
    const { addOrEditQueuedPlayedS, sendQueuedPlayedS } = useTrackingContext()

    const addOrEditQueuedPlayedSRef = useSelfUpdatingRef(addOrEditQueuedPlayedS)
    const sendQueuedPlayedSRef = useSelfUpdatingRef(sendQueuedPlayedS)

    const userData = useUserProfile()

    const trackPlayedSRef = useSelfUpdatingRef(trackPlayed)
    const isPreviewRef = useSelfUpdatingRef(isPreview)
    const durationRef = useSelfUpdatingRef(videoDurationSeconds)

    const sentRef = useRef(false)
    const lastPositionRef = useRef<number | null>(null)
    const maxPlayedSRef = useRef(0)
    const totalWatchSecondsRef = useRef(0)
    const rangesRef = useRef<WatchRange[]>([])

    const shouldTrack = useCallback(() => {
        return Boolean(
            trackPlayedSRef.current &&
                videoId &&
                videoUserId &&
                videoUserId !== userData?.id
        )
    }, [trackPlayedSRef, userData?.id, videoId, videoUserId])

    const updateMaxPlayedS = useCallback(
        (playedSeconds: number) => {
            if (!shouldTrack()) return
            if (!Number.isFinite(playedSeconds) || playedSeconds <= 0) return

            const current = Math.max(0, playedSeconds)
            const previous = lastPositionRef.current

            maxPlayedSRef.current = Math.max(maxPlayedSRef.current, current)

            if (previous !== null) {
                const delta = current - previous

                // Normal forward playback tick. Ignore seek jumps.
                if (delta > 0 && delta <= 5) {
                    totalWatchSecondsRef.current += delta
                    rangesRef.current.push({
                        start: previous,
                        end: current
                    })
                }
            }

            lastPositionRef.current = current
        },
        [shouldTrack]
    )

    const resetTrackingSession = useCallback(() => {
        sentRef.current = false
        lastPositionRef.current = null
        maxPlayedSRef.current = 0
        totalWatchSecondsRef.current = 0
        rangesRef.current = []
    }, [])

const queueAndSendPlayed = useCallback(() => {
    if (!shouldTrack()) return
    if (sentRef.current) return
    if (!videoId) return

    const videoDuration = Number(
        durationRef.current ?? 0
    )

    const mergedRanges = mergeRanges(
        rangesRef.current
    )

    const uniqueWatchSeconds =
        sumRanges(mergedRanges)

    const completionRate =
        videoDuration > 0
            ? Math.min(
                  100,
                  Math.round(
                      (
                          uniqueWatchSeconds /
                          videoDuration
                      ) * 10000
                  ) / 100
              )
            : 0

    const totalWatchSeconds =
        Math.round(
            totalWatchSecondsRef.current * 100
        ) / 100

    if (
        completionRate <= 0 &&
        totalWatchSeconds <= 0 &&
        maxPlayedSRef.current <= 0
    ) {
        return
    }

    sentRef.current = true

    addOrEditQueuedPlayedSRef.current({
        video_id: videoId,
        total_watch_seconds:
            totalWatchSeconds,
        unique_watch_seconds:
            Math.round(
                uniqueWatchSeconds * 100
            ) / 100,
        completion_rate:
            completionRate,
        video_duration_seconds:
            videoDuration,
        is_preview:
            isPreviewRef.current
    })

    sendQueuedPlayedSRef.current()
}, [
    shouldTrack,
    videoId,
    durationRef,
    isPreviewRef,
    addOrEditQueuedPlayedSRef,
    sendQueuedPlayedSRef
])

const queueAndSendPlayedRef =
    useSelfUpdatingRef(queueAndSendPlayed)

useEffect(() => {
    resetTrackingSession()

    return () => {
        queueAndSendPlayedRef.current()
    }
}, [
    url,
    videoId,
    resetTrackingSession,
    queueAndSendPlayedRef
])

useEffect(() => {
    const onUnload = (event: Event) => {
        if (
            event.type === 'visibilitychange' &&
            document.visibilityState !== 'hidden'
        ) {
            return
        }

        queueAndSendPlayedRef.current()
    }

    document.addEventListener(
        'visibilitychange',
        onUnload
    )

    window.addEventListener(
        'beforeunload',
        onUnload
    )

    return () => {
        queueAndSendPlayedRef.current()

        document.removeEventListener(
            'visibilitychange',
            onUnload
        )

        window.removeEventListener(
            'beforeunload',
            onUnload
        )
    }
}, [
    queueAndSendPlayedRef
])

return useMemo(
    () => ({
        updateMaxPlayedS,
        sendPlayedOnPause: queueAndSendPlayed,
        sendPlayedOnEnded: queueAndSendPlayed
    }),
    [
        updateMaxPlayedS,
        queueAndSendPlayed
    ]
)
}

// ENDS HERE
export const useProfileFanSessionTracking = (
    profileId: number,
    enabled: boolean
) => {
    const userData = useUserProfile()

    const userId = userData?.id ?? 0

    const canTrack =
        enabled &&
        profileId > 0 &&
        userId > 0 &&
        profileId !== userId

    const startTimeRef = useRef<dayjs.Dayjs | null>(null)
    const durationSentRef = useRef(false)

    const startSession = useCallback(() => {
        if (!canTrack) {
            startTimeRef.current = null
            durationSentRef.current = false
            return
        }

        startTimeRef.current = dayjs()
        durationSentRef.current = false
    }, [canTrack])

    const sendDuration = useCallback(() => {
        if (
            !canTrack ||
            durationSentRef.current ||
            !startTimeRef.current
        ) {
            return
        }

        durationSentRef.current = true

        const duration = dayjs().diff(
            startTimeRef.current,
            'second',
            true
        )

        startTimeRef.current = null

        if (duration <= 0) {
            return
        }

        void apiFanProfileSession(profileId, duration)
    }, [canTrack, profileId])

    const sendDurationRef = useSelfUpdatingRef(sendDuration)
    const startSessionRef = useSelfUpdatingRef(startSession)

    /*
     * Start a new session whenever:
     * - profile changes
     * - viewer changes
     * - tracking becomes enabled
     *
     * Send the previous session during cleanup.
     */
    useEffect(() => {
        if (!canTrack) {
            startTimeRef.current = null
            durationSentRef.current = false
            return
        }

        startSessionRef.current()

        return () => {
            sendDurationRef.current()
        }
    }, [
        canTrack,
        profileId,
        userId,
        sendDurationRef,
        startSessionRef
    ])

    /*
     * End the current session when the page becomes hidden.
     * Start a fresh session when the page becomes visible again.
     */
    useEffect(() => {
        if (!canTrack) {
            return
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                sendDurationRef.current()
                return
            }

            if (document.visibilityState === 'visible') {
                startSessionRef.current()
            }
        }

        const onBeforeUnload = () => {
            sendDurationRef.current()
        }

        document.addEventListener(
            'visibilitychange',
            onVisibilityChange
        )

        window.addEventListener(
            'beforeunload',
            onBeforeUnload
        )

        return () => {
            document.removeEventListener(
                'visibilitychange',
                onVisibilityChange
            )

            window.removeEventListener(
                'beforeunload',
                onBeforeUnload
            )
        }
    }, [
        canTrack,
        sendDurationRef,
        startSessionRef
    ])
}

export const useUserActivityFeedInfinite = (filter: {
  type?: string
  userId?: string
}) => {
  const {
    data,
    error,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    status
  } = useInfiniteQuery<IInfiniteNotifications, Error>(
    ['notifications', filter],
    async ({ pageParam = 1 }) => {
      if (!filter.userId) {
        throw new Error('Missing userId')
      }

      return await getUserActivity(pageParam, {
        ...filter,
        userId: filter.userId
      })
    },
    {
      getNextPageParam: lastPage => lastPage.nextCursor,
      enabled: !!filter.userId,
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  )

  return {
    data,
    error,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    status
  }
}
export const useGetUserProfile = (
  hasAccessToken: boolean,
  hasUserProfile: boolean
) =>
  useQuery<UserProfile, Error>('user-profile', getProfile, {
    enabled: hasAccessToken && !hasUserProfile,
  })



type UseStoryFeedOptions = {
  enabled?: boolean
  staleTime?: number
  queryKeySuffix?: string | number
}

export const useStoryFeed = (opts: UseStoryFeedOptions = {}) => {
  const {
    enabled = true,
    staleTime = 5_000,
    queryKeySuffix,
  } = opts

  const isAuthenticated = useAccessToken() !== ''
  const queryEnabled = Boolean(isAuthenticated && enabled)

  const queryKey: QueryKey = useMemo(() => {
    const key: (string | number)[] = ['storyFeed']

    if (queryKeySuffix != null) {
      key.push(queryKeySuffix)
    }

    return key
  }, [queryKeySuffix])

  const {
    data,
    isLoading,
    isSuccess,
    isError,
    refetch,
  } = useQuery<StoryFeedGroup[]>(
    queryKey,
    getStoryFeed,
    {
      enabled: queryEnabled,
      refetchOnWindowFocus: false,
      staleTime,
    }
  )

  const allStoryPosts: StoryFeedGroup[] =
    Array.isArray(data) ? data : []

  const reorderedStoryPosts = useMemo<StoryFeedGroup[]>(() => {
    if (!allStoryPosts.length) return []

    const seen: StoryFeedGroup[] = []
    const unseen: StoryFeedGroup[] = []

    for (const userBlock of allStoryPosts) {
      if (userBlock.all_stories_seen) {
        seen.push(userBlock)
      } else {
        unseen.push(userBlock)
      }
    }

    return [...unseen, ...seen]
  }, [allStoryPosts])

  return {
    allStoryPosts: queryEnabled ? allStoryPosts : [],
    reorderedStoryPosts: queryEnabled
      ? reorderedStoryPosts
      : [],
    refetchStories: refetch,
    isLoading: queryEnabled ? isLoading : false,
    isSuccess: queryEnabled ? isSuccess : false,
    isError: queryEnabled ? isError : false,
  }
}

type UseUserStoriesOptions = {
  enabled?: boolean
  staleTime?: number
  queryKeySuffix?: string | number
  creatorId: string
  refetchOnMount?: boolean
}

export const useUserStories = (opts: UseUserStoriesOptions) => {
  const {
    enabled = true,
    staleTime = 20_000,
    refetchOnMount = true,
    queryKeySuffix,
    creatorId,
  } = opts

  const isAuthenticated = useAccessToken() !== ''

  const queryEnabled = Boolean(
    isAuthenticated &&
    enabled &&
    typeof creatorId === 'string' &&
    creatorId.trim().length > 0
  )

  const queryKey: QueryKey = useMemo(() => {
    const key: (string | number)[] = ['userStories', creatorId]

    if (queryKeySuffix != null) {
      key.push(queryKeySuffix)
    }

    return key
  }, [creatorId, queryKeySuffix])

  const queryFn = useMemo(() => {
    return async () => await getAllStories(creatorId)
  }, [creatorId])

  const {
    data,
    isLoading,
    isFetched,
    refetch,
  } = useQuery(
    queryKey,
    queryFn,
    {
      enabled: queryEnabled,
      refetchOnWindowFocus: false,
      refetchOnMount,
      staleTime,
    }
  )

  const stories: Story[] = Array.isArray(data) ? data : []

  return {
    stories: queryEnabled ? stories : [],
    refetchStories: refetch,
    isLoading: queryEnabled ? isLoading : false,
    isFetched: queryEnabled ? isFetched : false,
  }
}
export const getPaginatedPost = async (postId: string) => {
    const data = await getSinglePost(postId)

    return {
        pages: [
            {
                page: {
                    data: {
                        data: [
                            {
                                ...data?.data
                            }
                        ]
                    }
                }
            }
        ]
    }
}
